import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './Beta.css';

function Beta() {
  // The apps link here with ?platform=android or ?platform=ios, so each opens on its own notes.
  // Anything else (or no parameter, as older app versions send) starts on All.
  const [searchParams] = useSearchParams();
  const requestedPlatform = searchParams.get('platform');
  const [platformFilter, setPlatformFilter] = useState(
    requestedPlatform === 'android' || requestedPlatform === 'ios' ? requestedPlatform : 'all'
  ); // 'all', 'android', 'ios'

  const releaseNotes = [
    {
      version: '2.3',
      date: 'In development',
      status: 'in-progress',
      // Mostly iOS: iOS 2.2 is already out, so these ship in the next iOS release, and most
      // Android counterparts are in 2.2 below. Android entries here are new in Android 2.3.
      // Flip to false when 2.3 goes live.
      hidden: true,
      note: 'The Podcasts tab remembers its sort, and a playlist shows which episode each row is.',
      changes: [
        {
          title: 'New: The Podcasts tab remembers how you sorted it',
          description: 'Choose Title, or any other order, and the Podcasts tab opens that way next time instead of going back to Recently Added. Only the sort is remembered; filters still start fresh.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Podcast episodes in a playlist looked like the podcast',
          description: 'Every episode in a playlist showed only its podcast’s cover and name, so two episodes of the same show were indistinguishable. Each row now shows the episode’s title with the podcast underneath, in grid and list view, and tapping it opens that episode. The playlist’s total time now counts each episode’s own length rather than the whole podcast’s.',
          platform: 'ios'
        },
        {
          title: 'Fixed: The episode title disappearing from the player',
          description: 'After you reopened Audra, the full player lost the title of the podcast episode you were in the middle of. It now keeps it.',
          platform: 'ios'
        },
        {
          title: 'New: Take a book out of Continue Listening',
          description: 'Long-press a book in Continue Listening on Home, or tap Finished on a book you have started, and choose Remove from Continue Listening. It stays in Show More, comes back as soon as you play it, and Undo puts it straight back.',
          platform: 'ios'
        },
        {
          title: 'New: Reset a book\u2019s progress',
          description: 'Tap Finished on a book you have started and choose Reset Progress to go back to the beginning. For a book with an ebook too, your reading position is kept.',
          platform: 'ios'
        },
        {
          title: 'New: Lock Screen Scrubbing',
          description: 'Turn it off in Settings \u2192 Playback and the progress bar on the lock screen, Control Center and CarPlay stops moving your place when it is brushed in a pocket. Play, pause and skip still work.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Opening Audra paused your other audio',
          description: 'Opening Audra no longer stops a video or song playing in another app. Audra takes over only when you press play.',
          platform: 'ios'
        },
        {
          title: 'Fixed: A second tap as Audra opens',
          description: 'A stray tap could land during the opening vibration. It is gone, and with Launch Haptic off nothing buzzes at all.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Playback settings reset after an update',
          description: 'Updating Audra could put your skip intervals, auto-rewind and other playback settings back to their defaults. They now carry over.',
          platform: 'ios'
        },
        {
          title: 'New: See how far you are through a series',
          description: 'A series now shows how many of its books you have finished, beside its book count and hours.',
          platform: 'ios'
        },
        {
          title: 'New: Sort bookmarks by newest',
          description: 'The \u2191\u2193 button on a book\u2019s Bookmarks tab switches between position in the book and the ones you made most recently, with how long ago each was made. Audra remembers your choice.',
          platform: 'ios'
        },
        {
          title: 'New: All your bookmarks in one place',
          description: 'The Lists tab now opens with Bookmarks: every bookmark across your library, grouped by book, newest first. Tap one to pick up right where you marked it.',
          platform: 'ios'
        },
        {
          title: 'Fixed: NEW on books you have already started',
          description: 'Books on Home no longer say NEW once you have started or finished them.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Finished books turning unfinished',
          description: 'Switching to another book without playing the one Audra had loaded could mark it unfinished or move it back to an older spot. Audra now leaves your progress alone unless you actually listen.',
          platform: 'ios'
        },
        {
          title: 'New: Tips for Continue Listening',
          description: 'Tap the tip under Continue Listening on Home to see how the row works: press and hold a book to remove it, find it again in Show More, and choose how far back the row goes. A button takes you straight to the setting. Tap the \u2715 to hide the tip, and bring it back any time from Settings \u2192 Account \u2192 Reset Tips.',
          platform: 'ios'
        },
        {
          title: 'New: Recent Activity is free',
          description: 'Everyone can now choose how far back Continue Listening goes \u2014 30, 60 or 90 days, or the Audiobookshelf default \u2014 in Settings \u2192 Library Management \u2192 Manage Home Screen. The rest of Home Screen customization stays in Audra Pro.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Removed books you couldn\u2019t get back to',
          description: 'With only a few books in progress, a book you removed from Continue Listening could not be reached from Home. Continue Listening now always ends with Show More, which lists every book you have in progress, removed ones included.',
          platform: 'ios'
        },
        {
          title: 'Improved: A camera on your Stats picture',
          description: 'Until you add a photo, the circle at the top of My Stats shows a small camera, so it is clear you can tap it to put your picture there.',
          platform: 'ios'
        },
        {
          title: 'Fixed: A downloaded book starting over',
          description: 'Coming back to Audra after a while away could send a paused downloaded book back to the beginning. It now keeps your place, and pressing play picks up where you left off.',
          platform: 'ios'
        },
        {
          title: 'New: Author photos and descriptions from your server',
          description: 'An author’s page now shows their photo and the description from your Audiobookshelf server, for everyone, and the Authors list shows each author’s photo beside their name, or their initials when there isn’t one. With AI Biographies on, Audra Pro writes one only for authors your server doesn’t describe.',
          platform: 'both'
        },
        {
          title: 'Improved: Series covers appear straight away',
          description: 'Opening Series now shows each series’ covers right away, including with Collapse Series on, instead of placeholders that filled in a moment later.',
          platform: 'ios'
        },
        {
          title: 'Improved: The series badge sits at the bottom of the cover',
          description: 'With Collapse Series on, a series in Books now shows its SERIES badge in the bottom corner of the cover, as on iPhone, instead of the top.',
          platform: 'android'
        },
        {
          title: 'Fixed: Your phone’s details on your server',
          description: 'Your listening sessions now tell your Audiobookshelf server the right Audra version, phone model and system version, and every phone gets its own entry. Some showed the server’s own version, an unknown phone, or shared one entry.',
          platform: 'both'
        },
        {
          title: 'Improved: Switching and combining libraries',
          description: 'Pick libraries at the top of the Library tab and Audra loads them once when you tap Done, showing the books it already has straight away. Books from a library you untick now leave the list.',
          platform: 'both'
        },
        {
          title: 'Improved: Pull to refresh',
          description: 'Pulling down on the Library tab now removes books deleted from your server and updates your progress, and a refresh that fails keeps what you were looking at. Series, collections, books, All Bookmarks and podcast pages can now be pulled to refresh too.',
          platform: 'both'
        },
        {
          title: 'Fixed: Sleep timer and downloads',
          description: 'Sleep at end of chapter now stops at the end of the chapter at any playback speed, and a download your server can’t send now shows as failed instead of finishing with a broken file.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Seeking from the lock screen',
          description: 'Dragging the seek bar on the lock screen, in the notification or in Android Auto now lands where you drag it in books made of several files. While casting, the lock screen shows your place in the book and previous/next move between chapters.',
          platform: 'android'
        },
        {
          title: 'Fixed: Books made of several files',
          description: 'Files you exclude in your server’s Manage Tracks are no longer played or downloaded, and books with same-named files in different folders (Disc 1, Disc 2) now download every file.',
          platform: 'both'
        },
        {
          title: 'Improved: Listening offline',
          description: 'Books you finish offline or during the closing credits now stay finished, and your place and listening time hold up better through flights, crashes and server restarts.',
          platform: 'both'
        }
      ]
    },
    {
      version: '2.2',
      date: 'September 23, 2026',
      status: 'now-live',
      // iOS brings Read Along; Android catches up on what iOS shipped in 2.1. The Android
      // entries landed after Android 2.1 (versionCode 40) went to Google Play on August 21, so
      // they ship here rather than there.
      // hidden hides the whole release; hideAndroid / hideIos hide one platform's entries while
      // the other is live. Set hideAndroid to false when Android 2.2 reaches Google Play.
      hidden: false,
      hideAndroid: false,
      hideIos: false,
      note: 'Android is live as of October 15 — it spent 20 days in Google Play review after the iOS release.',
      // Shown instead of note while one platform is hidden, since note describes both.
      singlePlatformNote: {
        ios: 'Read Along arrives on iOS.',
      },
      changes: [
        {
          title: 'New: Read Along — the narration, highlighted in your ebook',
          description: 'For a book that has both an ebook and an audiobook, Audra can follow the narrator through the text. The word being spoken is highlighted, the sentence or paragraph around it is tinted, pages turn themselves, and a double tap on any word jumps the audio there. Make the alignment yourself from the reader or from Settings → Read Along, choose how the highlight looks (fill, underline, both or none, six colours, three strengths), and align a whole library in one pass with Align all. Alignment happens entirely on your own device — nothing is sent anywhere — and if your Audiobookshelf account can upload, finished alignments can be published beside the book so everyone else on your server gets them without doing the work. ',
          descriptionSuffix: <>Full write-up at <Link to="/read-along">audrabooks.app/read-along</Link>.</>,
          platform: 'ios'
        },
        {
          title: 'New: Continue Listening shows what you are actually listening to',
          description: 'The Continue Listening row now follows the same rule your server does for what counts as in progress, and you can give it a recency window — Settings → Home Screen → Recent Activity — so a book you set down months ago stops holding a place near the front.',
          platform: 'ios'
        },
        {
          title: 'New: Keep the screen awake while you read',
          description: 'The ebook reader no longer lets the screen dim out from under you mid-paragraph. It is on by default, and there is a switch for it in the reader\u2019s own settings \u2014 Display \u2192 Keep Screen Awake \u2014 if you would rather have the battery.',
          platform: 'ios'
        },
        {
          title: 'New: Turn the launch haptic off',
          description: 'The vibration as Audra opens now has a switch \u2014 Settings \u2192 Appearance \u2192 Launch Haptic. On by default, off for anyone who would rather open the app in silence.',
          platform: 'ios'
        },
        {
          title: 'New: One set of playback speeds, in the app and in the car',
          description: 'The speed button in CarPlay cycles the same 0.8× to 2× tenth-step presets the app uses, instead of its own quarter steps, and the car shows the rate you actually picked.',
          platform: 'ios'
        },
        {
          title: 'New: Collections and playlists share a Lists tab',
          description: 'The Collections tab is now Lists, and holds your server collections and your own playlists together. Each row carries a mosaic of its covers, a count, and a label saying whether it is a playlist or a server collection, and a segmented row underneath the picker narrows it to one kind or the other.',
          platform: 'android'
        },
        {
          title: 'New: Make and manage playlists from the Lists tab',
          description: 'Create a playlist without going through Settings, and add to or edit one from its own screen. The collection and playlist sheets follow your theme rather than defaulting to blue.',
          platform: 'android'
        },
        {
          title: 'New: Download All on collections and playlists',
          description: 'Download All already worked on a series. It now works on a collection and on a playlist, and it keeps a podcast\u2019s episodes distinct, so a playlist holding two episodes of the same show downloads both.',
          platform: 'android'
        },
        {
          title: 'New: A year of listening, as a heatmap',
          description: 'The stats screen gains a grid of the last 365 days, each square shaded by how long you listened that day. It always shows the full year whatever period you have selected, and says so when a content filter is on, because the server cannot split a day map by content type.',
          platform: 'android'
        },
        {
          title: 'New: Show More on a Home row',
          description: 'A Home row with more in it than fits now ends in a Show More card that opens the whole thing. Continue Listening, Continue Reading and Finished open as dated lists, sorted and dated by when you actually last listened, last read, or finished.',
          platform: 'android'
        },
        {
          title: 'New: Add an episode that is only in the feed',
          description: 'Adding a podcast episode to a playlist needs the episode to exist on your server. Instead of stopping at \u2018Episode not yet available on the server\u2019, Audra now offers to fetch it for you first.',
          platform: 'android'
        },
        {
          title: 'New: Rolling or calendar periods in Stats',
          description: 'Choose how a stats period is measured. Rolling, the default, means the last 24 hours, 7 days, 30 days or 365 days. Calendar means since midnight, since the start of your week, since the 1st, since January. A caption under the period picker says which one you are looking at, and there is a toggle in the My Stats toolbar as well as a setting under Settings → Stats Settings, where you can also choose whether a week starts on Sunday or Monday. The charts and the year total follow it too.',
          platform: 'android'
        },
        {
          title: 'Fixed: Playlists that were missing, stale, or offered wrongly',
          description: 'Podcast playlists did not appear in the list at all, and playlists were read from only one of the two shapes the server sends them in. A playlist deleted on the server no longer lingers on the phone. And a book is only offered the playlists it can actually be added to.',
          platform: 'android'
        },
        {
          title: 'Fixed: Listening history on a book',
          description: 'A day from a different year now says which year it was.',
          platform: 'android'
        },
        {
          title: 'Fixed: Listening counted on the wrong day in Stats',
          description: 'An evening of listening could show up on the next day, and in the wrong hour, if the app synced the next morning. Stats now counts a session on the day it started, the same day your server and the heatmap use.',
          platform: 'android'
        },
        {
          title: 'New: The Podcasts tab remembers how you sorted it',
          description: 'Choose Title, or any other order, and the Podcasts tab opens that way next time instead of going back to Recently Added. Only the sort is remembered; filters still start fresh.',
          platform: 'android'
        },
        {
          title: 'Fixed: Podcast episodes in a playlist looked like the podcast',
          description: 'Every episode in a playlist showed only its podcast’s cover and name, so two episodes of the same show were indistinguishable. Each row now shows the episode’s title with the podcast underneath, and tapping it opens that episode.',
          platform: 'android'
        },
        {
          title: 'Fixed: The player names the episode',
          description: 'While a podcast episode plays, the full player now shows the episode’s title under the cover, and the mini player names the episode rather than just the show.',
          platform: 'android'
        },
        {
          title: 'Fixed: A podcast episode that would not resume',
          description: 'Coming back to an episode after Audra had been in the background for a while could stop with ‘No audio files available’ instead of playing. It now picks up the same episode where you left off, from the download if you have one.',
          platform: 'android'
        },
        {
          title: 'Fixed: Listening time lost on a patchy connection',
          description: 'On a slow or dropping connection — a drive, a train — Audra could mistake a stalled sync for the app being asleep and throw away the minutes you had actually listened, so your stats came up short. It now checks whether the book kept playing, and only discards time when it really did not.',
          platform: 'android'
        },
        {
          title: 'Fixed: Progress percentages that read low',
          description: 'A book 99.7% finished said 99%. Percentages are now rounded rather than cut off, everywhere they appear, Android Auto included.',
          platform: 'android'
        },
        {
          title: 'Fixed: A session that looked fourteen hours long',
          description: 'A book’s listening history read as though one sitting had run overnight. The second time on a session is now labelled Last synced rather than Session ended, the dates appear when a session’s two times fall on different days, the time you actually listened is spelled out, and each session is filed under the day it started.',
          platform: 'android'
        },
        {
          title: 'New: Continue Listening shows what you are actually listening to',
          description: 'The Continue Listening row now follows the same rule your server does for what counts as in progress, and you can give it a recency window — Settings → Home Screen → Recent Activity — so a book you set down months ago stops holding a place near the front. Everything older is still under Show More.',
          platform: 'android'
        },
        {
          title: 'New: Take a book out of Continue Listening',
          description: 'Long-press a book in Continue Listening on Home, or tap Finished on a book you have started, and choose Remove from Continue Listening. It stays in Show More, comes back as soon as you play it, and Undo puts it straight back.',
          platform: 'android'
        },
        {
          title: 'New: Reset a book\u2019s progress',
          description: 'Tap Finished on a book you have started and choose Reset Progress to go back to the beginning. For a book with an ebook too, your reading position is kept.',
          platform: 'android'
        },
        {
          title: 'New: Lock Screen Scrubbing',
          description: 'Turn it off in Settings \u2192 Playback and the progress bar on the lock screen and in the notification stops moving your place when it is brushed in a pocket. Play, pause and skip still work.',
          platform: 'android'
        },
        {
          title: 'New: Series show your progress',
          description: 'In a series, finished books carry a checkmark and books you are partway through show how far along you are. The header counts how many you have finished.',
          platform: 'android'
        },
        {
          title: 'New: Sort bookmarks by newest',
          description: 'The \u2191\u2193 button on a book\u2019s Bookmarks tab switches between position in the book and the ones you made most recently, with how long ago each was made. Audra remembers your choice.',
          platform: 'android'
        },
        {
          title: 'Fixed: The player not fitting on smaller screens',
          description: 'On shorter phones, or with a larger display size, the play controls could sit below the bottom of the player. It now fits without scrolling.',
          platform: 'android'
        },
        {
          title: 'New: All your bookmarks in one place',
          description: 'The Lists tab now opens with Bookmarks: every bookmark across your library, grouped by book, newest first. Tap one to pick up right where you marked it.',
          platform: 'android'
        },
        {
          title: 'Fixed: NEW on books you have already started',
          description: 'Books on Home no longer say NEW once you have started or finished them.',
          platform: 'android'
        },
        {
          title: 'New: Material You theme',
          description: 'Choose Material You in Settings → Appearance → App Theme and Audra takes its colours from your wallpaper, like the rest of your phone. It follows light and dark mode and the OLED black setting, and it is free. Needs Android 12 or later.',
          platform: 'android'
        },
        {
          title: 'New: Tips for Continue Listening',
          description: 'Tap the tip under Continue Listening on Home to see how the row works: press and hold a book to remove it, find it again in Show More, and choose how far back the row goes. A button takes you straight to the setting. Tap the \u2715 to hide the tip, and bring it back any time from Settings \u2192 Account \u2192 Reset Tips.',
          platform: 'android'
        },
        {
          title: 'New: Recent Activity is free',
          description: 'Everyone can now choose how far back Continue Listening goes \u2014 30, 60 or 90 days, or the Audiobookshelf default \u2014 in Settings \u2192 Library Management \u2192 Manage Home Screen. The rest of Home Screen customization stays in Audra Pro.',
          platform: 'android'
        },
        {
          title: 'Fixed: Removed books you couldn\u2019t get back to',
          description: 'With only a few books in progress, a book you removed from Continue Listening could not be reached from Home. Continue Listening now always ends with Show More, which lists every book you have in progress, removed ones included.',
          platform: 'android'
        },
        {
          title: 'New: A tip for Continue Reading',
          description: 'When tapping a book in Continue Reading opens the reader, a tip above the row says to press and hold for its other options. It also comes back with Reset Tips.',
          platform: 'android'
        },
        {
          title: 'Fixed: Series, Authors and library pages in light mode',
          description: 'The Series and Authors tabs, and the author, series, collection and filtered library pages, stayed black in light mode with dark text on top. They now follow light and dark mode like the rest of the app.',
          platform: 'android'
        },
        {
          title: 'Fixed: Blue buttons that ignored your theme',
          description: 'Buttons in pop-ups, text field cursors and a few other controls were a stock blue whatever theme you picked. They now use your theme\u2019s colour.',
          platform: 'android'
        },
        {
          title: 'Improved: Rounded menus',
          description: 'Every pop-up menu, like the one you get by pressing and holding a book on Home, now has rounded corners to match the rest of the app.',
          platform: 'android'
        },
        {
          title: 'New: Your picture on My Stats',
          description: 'Tap the circle at the top of My Stats and choose a photo, then pinch and drag to frame it. A small camera marks the circle until you do. Remove it from the same menu.',
          platform: 'android'
        },
        {
          title: 'New: Home screen widgets',
          description: 'Three widgets, each in two sizes. Continue Listening shows the book you\u2019re in with play, pause and skip, or just its cover. Listening Stats shows your hours, books finished and streak, and Listening Trend draws your week as a line. They follow your theme\u2019s colours.',
          platform: 'android'
        },
        {
          title: 'Fixed: A bump in the opening vibration',
          description: 'The haptic as Audra opened started smoothly and then knocked twice, on some launches but not others. A tab the app selected for itself while starting up was answering with a tap of its own, right on top of it. Tabs you tap still tap back; tabs the app moves no longer do.',
          platform: 'ios'
        },
        {
          title: 'Fixed: A book that would not start, or quietly started over',
          description: 'Opening a book you had never played could sit at 0:00 and go nowhere. A book you had played before could jump back to the beginning when the resume was slow to arrive. Both now wait for the audio to be ready and start where you left off.',
          platform: 'ios'
        },
        {
          title: 'Fixed: CarPlay restarting a book from the beginning',
          description: 'Reconnecting to the car, or coming back after a network drop, could restart the current book from 0:00 instead of resuming. It now rebuilds the stream properly and gives up on a dead one immediately rather than stalling for thirty seconds first.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Tapping a series in CarPlay said \u201cNo books here\u201d',
          description: 'A series in CarPlay now opens to its books.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Scrubbing from the lock screen or the car',
          description: 'With chapter-based progress on, dragging the scrubber landed at the wrong place in the book. It now lands where you dropped it.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Downloaded books waiting on a server they could not reach',
          description: 'A downloaded book used to pause for up to a minute trying to reach your server before it would play. It now plays straight away, which is the whole point of having downloaded it.',
          platform: 'ios'
        },
        {
          title: 'Fixed: A downloaded book that ignored the play button',
          description: 'If the audio route failed to start — a handoff to a speaker or a car that did not take — playback would sit on buffering and swallow every press. It now stops on a pause you can see, with a notice saying what happened.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Some audiobooks failed to begin playing',
          description: 'Books in newer AAC encodings sometimes failed to decode on the first try and gave up. Audra now retries, and sooner.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Podcast episodes lining up with the wrong entry',
          description: 'Episodes from a feed are now matched to the episodes on your server the same way the server matches them, so what you see in a feed is what you get on your shelf.',
          platform: 'ios'
        },
        {
          title: 'Fixed: One podcast episode download taking the others with it',
          description: 'Cancelling or clearing a single episode no longer empties the rest of that show out of the download queue.',
          platform: 'ios'
        },
        {
          title: 'Fixed: \u201cNo audio files\u201d when downloading from search',
          description: 'Downloading a book you reached from search or a cached list could fail outright. Audra now fetches the full book first.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Podcast libraries sync more gently',
          description: 'A large podcast library used to request every show at once on sync. It now works through them a few at a time.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Collections vanishing from the Lists tab',
          description: 'Opening a book and coming back left the Lists tab without its collections until you restarted. They stay put now.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Progress percentages that read low',
          description: 'A book 99.7% finished said 99%. Percentages are now rounded rather than cut off.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Audra quitting in the background on a Mac',
          description: 'Running the app on an Apple Silicon Mac, macOS would kill it while it sat suspended — so a long alignment or a download came back to nothing. It now stays put.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Listening counted on the wrong day',
          description: 'An evening’s listening could show up on the next day in My Stats, which also skewed the weekday chart, your favourite listening time and your streak. Every one of those now counts a session on the day you listened, matching your server and the heatmap on the same screen.',
          platform: 'ios'
        },
        {
          title: 'Fixed: A session that looked fourteen hours long',
          description: 'A book’s listening history read as though one sitting had run overnight. The second time on a session is now labelled Last synced rather than Session ended, the dates appear when a session’s two times fall on different days, and the time you actually listened is spelled out.',
          platform: 'ios'
        },
        {
          title: 'Fixed: CarPlay’s Home screen flickering as you listen',
          description: 'Every ten seconds the car redrew the whole Home screen — Recently Added and Discover included — to put back the same covers it already had. It now redraws only when something on it has actually changed.',
          platform: 'ios'
        },
        {
          title: 'Fixed: The book not starting when you get in the car',
          description: 'A play command from the car could arrive a moment before Audra had picked your book back up, and was dropped — so getting in sometimes took a second tap to start. It is now held and honoured as soon as the book is ready.',
          platform: 'ios'
        }
      ]
    },
    {
      version: '2.1',
      date: 'August 31, 2026',
      status: 'now-live',
      changes: [
        {
          title: 'New: Tap a series to open it',
          description: 'The series a book belongs to is now a link. Tap it on the book detail screen, or in the overflow sheet, and you land on that series with every book in it. On Android it works from ebooks too. A book whose series has no id shows the name greyed out rather than hiding it, so nothing shifts around under your thumb.',
          platform: 'both'
        },
        {
          title: 'New: Books and podcasts counted separately in Stats',
          description: 'Stats gains a content filter: see everything, only books, or only podcasts. Listening time, finished counts and the charts all follow the filter, and a new split row shows how your time divides between the two.',
          platform: 'android'
        },
        {
          title: 'New: A dropped connection no longer ends playback',
          description: 'When a stream stalls because the connection went away, the player now rebuilds it at the exact position the audio reached and carries on. If the connection cannot be recovered it pauses on the word it stopped at, rather than jumping backwards or going quiet with no explanation. Your listening time is accounted for correctly across the whole recovery, and the clock stops while the audio is stalled instead of counting silence as listening.',
          platform: 'ios'
        },
        {
          title: 'New: Animated page turns',
          description: 'The EPUB reader moves to Readium 3.11, and page turns animate again instead of snapping. The reader also stops running a local HTTP server it no longer needed.',
          platform: 'ios'
        },
        {
          title: 'New: Home Screen widgets',
          description: 'Three widgets, each in a small and a medium size. Continue Listening shows the book you are on and how much is left, with play, pause and skip on the medium size. Listening Stats shows hours listened, books finished and your current streak. Listening Trend draws your week as a line, the same shape as the chart in My Stats. All three follow your theme, and tapping one opens straight to the player or to Stats.',
          platform: 'ios'
        },
        {
          title: 'New: Siri and Shortcuts',
          description: 'Audra answers Siri with nothing to set up. Ask it to play or pause, to keep listening where you left off, to play a book by name, to skip forward or back, to set or cancel a sleep timer, to set the playback speed, or to open a section of the app. Each one is also a block you can drop into your own shortcuts.',
          platform: 'ios'
        },
        {
          title: 'New: Your library in Spotlight',
          description: 'Your books are indexed into iPhone search. Pull down on the Home Screen, type a title or an author, and tapping the result opens that book in Audra. Series are indexed too, so a series you have collapsed opens to its own screen.',
          platform: 'ios'
        },
        {
          title: 'New: Collections and playlists share a Lists tab',
          description: 'The Collections tab is now Lists, and holds your server collections and your own playlists together, each shown as a mosaic of its covers. Chips across the top narrow it to All, Collections or Playlists, and a playlist opens the same detail screen a collection does.',
          platform: 'ios'
        },
        {
          title: 'New: Make a playlist without going to Settings',
          description: 'New Playlist used to be reachable only through Settings. The Lists tab now carries a + of its own, and a playlist can be added to and edited from its own screen.',
          platform: 'ios'
        },
        {
          title: 'New: Download All on collections and playlists',
          description: 'Download All already worked on a series. It now works on a collection and on a playlist.',
          platform: 'ios'
        },
        {
          title: 'New: A year of listening, as a heatmap',
          description: 'My Stats gains a grid of the last 365 days, each square shaded by how long you listened that day, above a count of the days you listened in the past year. Tap a square for the date and the time.',
          platform: 'ios'
        },
        {
          title: 'New: Rolling or calendar periods in Stats',
          description: 'Choose how a stats period is measured. Rolling, the default, means the last 24 hours, 7 days, 30 days or 365 days. Calendar means since midnight, since the start of your week, since the 1st, since January. The card says which one you are looking at, and there is a toggle in the My Stats toolbar as well as a setting under Stats Settings.',
          platform: 'ios'
        },
        {
          title: 'New: Show More on a Home row',
          description: 'A Home row with more in it than fits now ends in a Show More card that opens the whole thing. Continue Listening, Continue Reading and Finished open as dated lists \u2014 when you last listened, when you last read, when you finished \u2014 rather than another wall of covers.',
          platform: 'ios'
        },
        {
          title: 'New: Lists in CarPlay',
          description: 'The car showed collections only, under the older name. It now shows Lists, playlists included, and each row says whether it is a playlist or a server collection so you know before you open it.',
          platform: 'ios'
        },
        {
          title: 'New: A profile photo on My Stats',
          description: 'Tap the avatar at the top of My Stats to pick a photo, then pinch and drag to frame it inside the circle before saving. It is kept in your iCloud account, so it follows you to your other devices.',
          platform: 'ios'
        },
        {
          title: 'New: Tap Total Time to change the unit',
          description: 'Total Time on My Stats reads in days. Tap it for hours, tap again for minutes \u2014 17,580 minutes lands differently to 12d 4h.',
          platform: 'ios'
        },
        {
          title: 'Improved: The library opens and scrolls far faster',
          description: 'The biggest change in this release. The library opens and scrolls far faster, and opening a book draws straight away instead of pausing first.',
          platform: 'both'
        },
        {
          title: 'Improved: A much smaller download',
          description: 'A new install now downloads 9.58 MB, down from 17.2 MB \u2014 7.64 MB smaller, a little under half.',
          platform: 'android'
        },
        {
          title: 'Improved: Stats appear immediately',
          description: 'The headline numbers appear as soon as you open Stats, with the detail filling in behind them. Going back to a period you have already looked at is instant.',
          platform: 'both'
        },
        {
          title: 'Improved: Series load faster, and are no longer cut off',
          description: 'The series list stopped truncating at 1000 on iOS, and now decodes only what is actually on screen. On Android the series endpoint is paged, served from cache, and the remaining pages are swept in the background, so a large library no longer stalls on the way in.',
          platform: 'both'
        },
        {
          title: 'Improved: Your place in the library survives a tab switch',
          description: 'Leaving the Library tab and coming back used to throw away your scroll position and start again from the top. It is kept now.',
          platform: 'ios'
        },
        {
          title: 'Improved: A server that is down is reported in seconds',
          description: 'The app briefly gives a server the benefit of the doubt after a network change, because pings fail for a few seconds for reasons that have nothing to do with the server. But a server that answers with an error is a definite answer, not a maybe. It used to spend up to a minute insisting everything was fine while every request came back failing.',
          platform: 'ios'
        },
        {
          title: 'Improved: Covers and details settle instead of popping in',
          description: 'The cached cover is painted behind the detail screen while the sharp one loads, the author and narrator appear from cache rather than waiting on a second request, and the loading spinner holds back briefly so a fast open never flashes one. Covers already fetched for the grid are reused on series, author and collection screens instead of being downloaded again.',
          platform: 'android'
        },
        {
          title: 'Improved: Podcast shows in Android Auto are a cover grid',
          description: 'The Shows list in the car is now cover art rather than a column of bare titles.',
          platform: 'android'
        },
        {
          title: 'Improved: Player artwork uses less memory',
          description: 'Cover art is now downsampled as it is decoded rather than after, so the player holds a fraction of what it used to.',
          platform: 'android'
        },
        {
          title: 'Improved: CarPlay opens on what it already knows',
          description: 'The car draws its tabs from what is already on the phone instead of waiting on the server, and it no longer announces an empty library while it is still loading. List icons follow your accent colour, having been black whatever your theme.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Media controls vanished from the notification, lock screen and Android Auto',
          description: 'Playback worked, but the buttons were missing from the notification shade, the lock screen and Android Auto. They are back.',
          platform: 'android'
        },
        {
          title: 'Fixed: Chapter progress was ignored on streamed books',
          description: 'The notification, lock screen and Android Auto seek bar always showed whole-book progress on a book you were streaming, whatever your chapter setting said. Downloaded books were unaffected.',
          platform: 'android'
        },
        {
          title: 'Fixed: Downloaded books had no chapter seek bar offline',
          description: 'Chapters are now stored alongside the download, so a book you took offline keeps its chapter seek bar with no connection.',
          platform: 'android'
        },
        {
          title: 'Fixed: The artist line was blank in Android Auto and on Cast',
          description: 'The now playing artist is now read from either shape the server sends it in, and starts from the chapter information already fetched instead of waiting.',
          platform: 'android'
        },
        {
          title: 'Fixed: Library counts were wrong on a cold open',
          description: 'The header could say 0 series while the list underneath was showing them. The count now follows the cache as it fills, rather than sampling it once before the refresh has landed. Counts also sum correctly across several selected libraries and follow the active filter.',
          platform: 'android'
        },
        {
          title: 'Fixed: Switching servers left Pro features on the old server',
          description: 'Switching connections now hands the Pro check the server you actually switched to.',
          platform: 'android'
        },
        {
          title: 'Fixed: Series not found when opening a collapsed row',
          description: 'Tapping a collapsed series row in the Books tab could report the series as missing when its cache was empty, and series detail could come back truncated for the same reason.',
          platform: 'android'
        },
        {
          title: 'Fixed: The author filter did nothing',
          description: 'Filtering by author was being applied by neither the server nor the app, so it silently returned everything.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Refreshing the library erased chapter and audio-file counts',
          description: 'Refreshing the library could wipe the chapter and audio-file details from a book\'s screen. They stay put now.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Stats could show the wrong numbers after filtering',
          description: 'Filtered results are now derived from the unfiltered stored copy rather than saved over it, so a filtered view can no longer be mislabeled and served back as the full picture. The progress fetch behind the finished counts is cached and split by content type.',
          platform: 'both'
        },
        {
          title: 'Fixed: A session with no date stopped stats loading',
          description: 'One undated listening session used to halt pagination partway through, leaving the totals short.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Another account library could linger after signing in',
          description: 'Signing in as a different user now clears the previous account cached library, progress and stats rather than leaving them to show through.',
          platform: 'android'
        },
        {
          title: 'Fixed: Filter values with special characters returned nothing',
          description: 'A filter containing a special character returned nothing at all. Clearing a filter now puts the header counts back too.',
          platform: 'android'
        },
        {
          title: 'Fixed: The Podcasts tab in Android Auto repeated itself',
          description: 'The tab duplicated its contents, and could come back wrong on the very first request after connecting.',
          platform: 'android'
        },
        {
          title: 'Fixed: Finished podcast episodes were counted as books',
          description: 'Books Completed on the stats screen included episodes. Episodes now have a card of their own, and both cards open a list whose rows tap through to the book or the episode.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Listening history on a book',
          description: 'A day from a different year now says which year it was, and the history section loads when it is the only section on the screen rather than waiting for something else to load first.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Playlists that were missing, stale, or offered wrongly',
          description: 'Podcast playlists did not appear in the list at all. A playlist deleted on the server no longer lingers on the phone. And a book is only offered the playlists it can actually be added to.',
          platform: 'ios'
        },
        {
          title: 'Fixed: A downloaded book could refuse to play until you relaunched',
          description: 'If the player lost hold of a downloaded book there was no way back short of quitting the app. It recovers on its own now.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Smart download fetched ahead on the wrong thing',
          description: 'Downloading ahead while you listened to a podcast targeted the show rather than the episode you were playing.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Reordering your Home sections froze the list',
          description: 'Once you had dragged a Home section into a new order, Manage Home Screen stopped showing any section added since \u2014 the podcast ones had no row and no toggle. Your arrangement is kept, and newer sections are added to the end of it.',
          platform: 'ios'
        },
        {
          title: 'Fixed: The download bar ignored your theme',
          description: 'The download bar and its badge were blue whatever colour the rest of the app was.',
          platform: 'ios'
        },
        {
          title: 'New: What\u2019s New shows the releases you skipped',
          description: 'If you were a version or two behind, the What\u2019s New screen showed only the release you had just been given. It now shows everything you missed on the way.',
          platform: 'android'
        },
        {
          title: 'Fixed: A tab lost your place',
          description: 'Leaving a tab by way of a tab already open underneath it threw away where you were in the first one.',
          platform: 'android'
        }
      ]
    },
    {
      version: '2.0',
      date: 'August 11, 2026',
      status: 'now-live',
      note: 'Android is live as of August 21 \u2014 it spent nine days in Google Play review after the iOS release.',
      changes: [
        {
          title: 'New: Podcasts',
          description: 'Podcasts are now a first-class content type on Android. Browse your podcast library, search for and add shows, follow and unfollow them, and play or download episodes to the server, the device, or both. Home gains podcast carousels and an Episodes tab of the newest episodes across every show you follow, and auto-download keeps the newest unplayed ones waiting. Android Auto gets a Podcasts tab alongside Home, Library and Downloads: newest episodes first, a Shows list underneath, and downloaded episodes play with no signal. Not a podcast listener? Show Podcasts, under Settings, hides all of it — in the app and in the car.',
          platform: 'android'
        },
        {
          title: 'New: Multiple server connections',
          description: 'Save several Audiobookshelf logins — including two accounts on the same server — and switch between them from the new Server Connections screen. Each connection keeps its own credentials and its own downloads, so nothing bleeds between them.',
          platform: 'android'
        },
        {
          title: 'New: Download ebooks for offline reading',
          description: 'Ebooks can be downloaded just like audiobooks, with their own rows and badges in Downloads. Opening an ebook now keeps a copy automatically so it is there the next time you have no signal, and the reader always prefers the copy on your device. Would rather not keep every book you open? Turn off Keep Ebooks You Open, in Download Settings, and the download button comes back so you can choose them yourself.',
          platform: 'both'
        },
        {
          title: 'New: Listen without leaving the page',
          description: 'On a book you own as both an ebook and an audiobook, a Listen button in the reader starts the audiobook exactly where it left off, without hunting for the book again. The player opens over the page you were reading, and closing it puts you straight back on that page.',
          platform: 'both'
        },
        {
          title: 'New: Mark an ebook as read or unread',
          description: 'Mark a book read or unread from the Read button on its detail screen, or by long-pressing it in the Continue Reading carousel on Home. On a book you own in both formats this only affects the reading side — it never marks the audiobook finished or disturbs its position.',
          platform: 'both'
        },
        {
          title: 'New: Listening history for each book',
          description: 'Every book now has a history tab showing your past listening sessions — when you listened and for how long. This was announced for Android once before and did not make it into a release; it genuinely arrives here.',
          platform: 'android'
        },
        {
          title: 'New: Books you are both reading and listening to appear in both rows',
          description: 'A book you own in both formats used to show up in only one of Continue Reading or Continue Listening, hiding your progress in the other. Both rows are now worked out on the device, so such a book appears in both — each at its own correct place and percentage.',
          platform: 'both'
        },
        {
          title: 'New: Redesigned Continue Listening and Continue Reading',
          description: 'Continue Reading now draws each book as a small hardback with a spine and boards. Continue Listening picks up the colour of the book\'s own cover art and carries a resume button that doubles as the progress bar, showing how much is left.',
          platform: 'both'
        },
        {
          title: 'New: Download All for a series',
          description: 'A Download All button on a series queues every book in it for download in one tap.',
          platform: 'android'
        },
        {
          title: 'New: Collapse series in the library',
          description: 'Books in a series can now group into a single row with a SERIES badge and a count, so a long series takes one slot in your library instead of twenty. It is on by default — turn off Collapse Series, in Library Display settings, to go back to seeing every book on its own.',
          platform: 'android'
        },
        {
          title: 'New: A-Z headers in the library',
          description: 'Sorting your library by title or author now shows alphabet headers inline as you scroll, making it much easier to find your place in a large collection.',
          platform: 'android'
        },
        {
          title: 'New: Tap genres, tags and narrators',
          description: 'Genres, tags and narrators on a book are now tappable and open a list of everything that matches. Narrators can also show an AI-written biography, the same opt-in feature authors already had.',
          platform: 'android'
        },
        {
          title: 'New: Choose how undownloaded books look offline',
          description: 'A new setting controls whether books you have not downloaded are shown normally, dimmed, or hidden while you are offline. An offline indicator in the tab bar tells you whether you have no connection or your server is simply unreachable.',
          platform: 'android'
        },
        {
          title: 'New: Progress recorded offline is kept until it reaches your server',
          description: 'Listening and reading done with no connection is now written to a durable queue on the device and retried until the server has it — surviving even if the app is closed in the meantime. Your device no longer loses an argument with a server that has an older position than you do.',
          platform: 'both'
        },
        {
          title: 'Fixed: Progress bar stuck at the start of every track',
          description: 'On audiobooks made of many MP3 or M4A files, the lock screen and Android Auto progress bar would stick a few seconds in and snap back to zero, over and over, while the audio itself played perfectly. Single-file M4B books were unaffected. The bar now tracks the whole book properly. Reported in issue #114.',
          platform: 'android'
        },
        {
          title: 'Fixed: The chapter progress bar ignored your setting until you restarted',
          description: 'Choosing whether the notification and Android Auto progress bar shows the current chapter or the whole book only took effect after force-quitting the app, and even then it sometimes did not. Crossing into a new chapter could leave the bar pinned at the far end until something else happened to refresh it. Both are fixed: the bar now updates the moment you change the setting, and re-scales cleanly at every chapter boundary.',
          platform: 'android'
        },
        {
          title: 'Fixed: Downloaded books always showed a whole-book progress bar',
          description: 'Chapter mode worked while streaming but never on a downloaded book, because chapters were not saved alongside the download. Downloads now keep their chapters, so the chapter progress bar works with no connection at all. Books already on your device repair themselves the next time you play them online — there is no need to download anything again.',
          platform: 'android'
        },
        {
          title: 'Fixed: Listening done offline could vanish',
          description: 'Listening to a downloaded book with no connection could lose your progress entirely rather than syncing it once you were back online. Reported in issue #127.',
          platform: 'android'
        },
        {
          title: 'Fixed: Position lost after a drive unless you reopened the app',
          description: 'Progress made in Android Auto could be lost if you finished your drive without opening the app afterwards. The final position is now saved on the device and uploads itself in the background. Reported in issue #119.',
          platform: 'android'
        },
        {
          title: 'Fixed: One book\'s position saved against another book',
          description: 'In some cases a tap in Android Auto could file the position you had reached in one book against a different book entirely. Positions are now always recorded against the book actually playing.',
          platform: 'android'
        },
        {
          title: 'Fixed: Chapter info and CarPlay artwork disappearing offline',
          description: 'While offline, the chapter name, the chapter progress bar and the Timeline button could all vanish from Now Playing, and CarPlay showed no cover art. Both came down to the app taking a different route to the same information when offline, and losing part of it on the way. Reported in issue #116.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Listening position snapping back towards the beginning',
          description: 'A resume that had not finished landing could be written back as your real position, throwing away hours of listening. This was sharpest in CarPlay. Positions are now only recorded once playback is confirmed to be where it should be.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Un-finishing an audiobook erased your place in the ebook',
          description: 'On a book you own in both formats, marking the audiobook as not finished wiped the whole progress record — including the page you had reached in the ebook. Only the audiobook\'s own progress is cleared now.',
          platform: 'both'
        },
        {
          title: 'Fixed: A finished audiobook hid a half-read ebook',
          description: 'Finishing the audiobook of a book you were also reading removed it from Continue Reading, even with most of the ebook still unread. It now stays, at its correct reading position.',
          platform: 'android'
        },
        {
          title: 'Fixed: Chapters shown as Upcoming on books listened to elsewhere',
          description: 'Chapter rows on a book\'s detail screen ignored progress made on another device, showing chapters you had already listened to as still upcoming.',
          platform: 'android'
        },
        {
          title: 'Fixed: Turning a page could rewind the audiobook',
          description: 'On a book you own in both formats, reading could interfere with the audiobook half — un-finishing it, or moving its position backwards. Reading and listening now keep their own places entirely.',
          platform: 'android'
        },
        {
          title: 'Fixed: Home went blank when your server was unreachable',
          description: 'If your server could not be reached, every carousel on Home disappeared rather than showing what was already loaded. Home now keeps its content and tells you what is wrong.',
          platform: 'android'
        },
        {
          title: 'Fixed: Downloads left behind after signing out',
          description: 'Downloaded audiobooks stayed on the device after logging out, so a second person signing in on the same phone would find them still there and still playable. Signing out now removes that account\'s downloads and tells you how much space it will free.',
          platform: 'android'
        },
        {
          title: 'Fixed: Sorting by author did almost nothing',
          description: 'Author sorting was quietly broken across most of the library, because the list the app fetched did not include author names at all.',
          platform: 'android'
        },
        {
          title: 'Fixed: Automatic cleanup could delete a book you had not finished',
          description: 'The rule that tidies up old downloads could remove a book you were partway through — and was more likely to do so while you were offline, which is exactly when you needed it. It now never removes something you have not finished.',
          platform: 'android'
        },
        {
          title: 'Fixed: Filtering or sorting broke offline browsing',
          description: 'Choosing a sort order or filter once could leave the library permanently unable to show anything offline.',
          platform: 'android'
        },
        {
          title: 'Fixed: A burst of podcast downloads at once',
          description: 'Auto-download could start dozens of episode downloads simultaneously instead of respecting the limit. It now downloads only the newest unplayed episodes, a few at a time. Reported in issue #122.',
          platform: 'both'
        },
        {
          title: 'Fixed: Deleting a downloaded ebook made the book unopenable',
          description: 'Removing an ebook\'s download and then tapping the book again reported that the file was missing, even though your server still had it.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Offline reading could be lost when reopening a book',
          description: 'Reading with no connection, then playing the audiobook elsewhere and reopening the book, could discard the reading you had done offline.',
          platform: 'android'
        },
        {
          title: 'Fixed: The offline confirmation was hidden behind the reader',
          description: 'The message confirming a book had been kept for offline reading drew behind the reader itself and was never actually seen. Reported in issue #65.',
          platform: 'ios'
        },
        {
          title: 'Fixed: A signed-in library selection could be silently forgotten',
          description: 'Signing in again could quietly collapse a multi-library selection down to one library, with the rest of your books simply absent and nothing explaining why.',
          platform: 'android'
        },
        {
          title: 'Improved: A book resumes at the same place wherever you tap it',
          description: 'The same book could previously resume at three different positions depending on whether you started it from Home, the Library, or Android Auto. All routes now agree.',
          platform: 'android'
        },
        {
          title: 'Improved: Reader controls stay readable on pale pages',
          description: 'The search and close icons and the seek bar in the reader used to be plain white and could disappear against a light page. They now sit on a subtle shade, and the overlay behind the controls is darker.',
          platform: 'android'
        },
        {
          title: 'Improved: Reader settings are a full screen',
          description: 'The font and appearance settings moved from a bottom sheet to a full screen, fixing the jitter while dragging and the options being unreachable on smaller screens or at large font sizes.',
          platform: 'android'
        },
        {
          title: 'Improved: Downloads recover from being stuck',
          description: 'Downloads left half-finished by a force-quit or an interrupted transfer are now picked up and resumed automatically, and downloads waiting for WiFi are parked and resumed when WiFi returns instead of failing.',
          platform: 'ios'
        },
        {
          title: 'Improved: Signing in twice to one server no longer duplicates it',
          description: 'Signing in again using a slightly different address for the same server used to create a second, separately tracked connection. Duplicates are now merged and their downloads kept.',
          platform: 'ios'
        },
        {
          title: 'Improved: Clearer podcast download choices',
          description: 'The prompt asking where to save an episode now explains plainly what saving to your server, your device, or both actually does, and reads the same way everywhere it appears.',
          platform: 'android'
        },
        {
          title: 'Improved: Podcast episode progress and time remaining',
          description: 'Episode progress bars now fill correctly, and tapping the duration badge switches between real time remaining and time adjusted for your playback speed.',
          platform: 'android'
        },
        {
          title: 'Improved: Podcasts keep working during a server outage',
          description: 'The Podcasts tab now keeps showing your last-loaded list when your server is briefly unreachable, and says which of "you are offline" or "your server cannot be reached" has actually happened.',
          platform: 'both'
        },
        {
          title: 'Improved: Offline and server-down states shown consistently',
          description: 'The Library, Settings, Downloads and Stats screens now show the same connection status that Home already did, so the app agrees with itself about whether you are offline.',
          platform: 'android'
        }
      ]
    },
    {
      version: '1.2.8',
      date: 'May 29, 2026',
      status: 'now-live',
      changes: [
        {
          title: 'New: Multiple server connections',
          description: 'Add and switch between multiple Audiobookshelf connections from Settings, each with its own credentials and an optional alternative URL for when you\'re on a different network. Downloads are now scoped to the connection they came from.',
          platform: 'ios'
        },
        {
          title: 'New: Tap authors, narrators, genres, and tags on the book page',
          description: 'Author and narrator names on a book\'s detail page are now tappable links — tap an author to open their page, or tap a narrator to see everything they\'ve narrated in your library. Genres and tags are tappable too, so you can jump straight to everything that shares them.',
          platform: 'ios'
        },
        {
          title: 'Improved: Tidier genres and tags on the book page',
          description: 'Genres and tags on a book\'s detail page are now centered and evenly aligned for a cleaner, more balanced look.',
          platform: 'ios'
        },
        {
          title: 'New: AI narrator biographies',
          description: 'Audra can now generate short AI biographies for narrators, not just authors. The setting has been renamed to cover both — turn it on or off in Settings.',
          platform: 'ios'
        },
        {
          title: 'New: Download All for a series',
          description: 'A Download All button in the series view downloads every book in that series in one tap.',
          platform: 'ios'
        },
        {
          title: 'New: Alphabet headers in the library',
          description: 'When your library is sorted by Title or Author (A–Z or Z–A), inline alphabet section headers now appear as you scroll, making it easier to find your place in a large library.',
          platform: 'ios'
        },
        {
          title: 'Improved: Clearer series badges in the library',
          description: 'Collapsed series now show a clear "SERIES" badge in place of the old stacked-books icon.',
          platform: 'ios'
        },
        {
          title: 'Improved: Cleaner remaining-time labels on the player',
          description: 'Remaining-time labels on the player timeline are now prefixed with a minus sign and spaced consistently for a tidier look.',
          platform: 'ios'
        },
        {
          title: 'Improved: Smoother player seek bar',
          description: 'The player timeline has been rebuilt to remove the jumpiness while seeking — the scrubber now tracks your finger smoothly as you drag, and the time keeps ticking right after you seek.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Garbled characters in book descriptions',
          description: 'Stray replacement characters that appeared in some book descriptions are now repaired so descriptions read cleanly.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Lost progress when starting playback offline',
          description: 'Fixed an edge case — common when CarPlay launches before the network is ready — where starting a session while offline could lose your saved position. Your progress is now preserved and synced once you\'re back online.',
          platform: 'ios'
        }
      ]
    },
    {
      version: '1.2.7',
      date: 'May 10, 2026',
      status: 'now-live',
      changes: [
        {
          title: 'New: Auto-play next book in series',
          description: 'When a book ends, Audra can automatically load and play the next book in the same series. Off by default — enable it in Settings > Playback. Skipped when a sleep timer is active.',
          platform: 'ios'
        },
        {
          title: 'New: Collapse Series in Library',
          description: 'Group books in a series into a single tappable entry in the library list, mirroring the Audiobookshelf server\'s collapse-series mode. Tap a series to view all of its books. Toggle it in Settings > Library Management.',
          platform: 'ios'
        },
        {
          title: 'New: Offline Library Behavior setting',
          description: 'Choose how the library looks when you\'re offline: Show All, Hide Unavailable (filter out books you haven\'t downloaded), or Dim Unavailable (show undownloaded books at reduced opacity, non-tappable). New installs default to Dim Unavailable. Find it in Settings > Library Management.',
          platform: 'ios'
        },
        {
          title: 'Smoother library scrolling',
          description: 'Cover art is now downsampled to cell size, pre-masked with rounded corners, and prefetched ahead of your scroll. Decoding runs on a concurrent queue with in-flight deduplication so prefetch and visible cells no longer block each other. The library grid stays smooth even with thousands of books.',
          platform: 'ios'
        },
        {
          title: 'Live offline updates across the app',
          description: 'Home carousels, series detail, author detail, and collection detail now update instantly when you go offline, come back online, change your offline behavior setting, or download a new book — no more nav-away-and-back to refresh.',
          platform: 'ios'
        },
        {
          title: 'Offline status icon in tab toolbars',
          description: 'A small icon now appears in the toolbar of the Library, Downloads, Podcasts, Stats, and Settings tabs when you\'re offline or the server is unreachable. Tap it for a quick explanation of the state. The Home banner is unchanged.',
          platform: 'ios'
        },
        {
          title: 'Faster recovery from network blips',
          description: 'The Server Unavailable banner now clears on its own within seconds of reconnecting — no pull-to-refresh required. Connectivity checks adapt their cadence based on state, polling more often while the banner is up and backing off once everything\'s healthy.',
          platform: 'ios'
        },
        {
          title: 'Downloaded books visible in series detail when offline',
          description: 'Opening a collapsed-series row offline now shows the books you have downloaded from that series, rendered from cached metadata, instead of an empty list.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Server Unavailable banner stuck after airplane-mode toggle',
          description: 'Toggling airplane mode could leave the orange Server Unavailable banner stuck on screen because back-to-back network notifications cancelled the reachability check. Network transitions now post immediately and recovery runs reliably.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Auto-resume failing after switching between books and podcasts',
          description: 'Playing a podcast and then switching to a book could leave the previous podcast episode ID attached to the book\'s saved state, causing a 400 error on auto-resume the next time you opened the app. Fixed.',
          platform: 'ios'
        }
      ]
    },
    {
      version: '1.2.6',
      date: 'April 28, 2026',
      status: 'now-live',
      changes: [
        {
          title: 'New: Listening history per book',
          description: 'A new History tab on each book\'s detail page shows your listening sessions — when you listened, how long each session was, and your progress over time.',
          platform: 'ios'
        },
        {
          title: 'New: CarPlay chapter controls and speed',
          description: 'CarPlay\'s Now Playing screen now includes chapter skip buttons, a chapter list, and playback speed controls — full control without picking up your phone.',
          platform: 'ios'
        },
        {
          title: 'Fixed: CarPlay blank Now Playing screen on resume',
          description: 'Resuming an audiobook from CarPlay no longer shows a blank Now Playing screen. Also fixed playback speed not being restored correctly on resume.',
          platform: 'ios'
        }
      ]
    },
    {
      version: '1.2.5',
      date: 'April 23, 2026',
      status: 'now-live',
      changes: [
        {
          title: 'Improved: Offline experience',
          description: 'Downloaded books now appear on the Home screen when offline. Chapter lists remain visible in book detail without a server connection. The app also skips unnecessary server refreshes when you return to it while offline.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Auto-resume not loading downloaded books when offline',
          description: 'Downloaded books now load and resume correctly when the app restarts without a network connection.',
          platform: 'ios'
        },
        {
          title: 'Fixed: ebook progress not displaying in Continue Reading carousel',
          description: 'ebook reading progress now shows correctly on book covers in the Continue Reading carousel on the Home screen.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Android Auto restarting books from the beginning',
          description: 'When Android Auto rebound after switching to another app (like Waze), books would sometimes restart from the beginning instead of resuming where you left off. The app now falls back to your locally saved position when the server has no progress record.',
          platform: 'android'
        },
        {
          title: 'Fixed: Android Auto taking 30–40 seconds to load a book',
          description: 'Cover art processing was happening on the main thread per track, causing significant delays when loading books — especially multi-file audiobooks. Loading is now much faster.',
          platform: 'android'
        },
        {
          title: 'Fixed: Progress reverting to an earlier position after the app is idle for hours',
          description: 'If the system killed the app while it was paused and idle, your playback position could revert the next time you opened the app. The app now correctly saves your position to the server before shutting down.',
          platform: 'android'
        }
      ]
    },
    {
      version: '1.2.4',
      date: 'March 13, 2026',
      status: 'now-live',
      changes: [
        {
          title: 'Fixed: Home screen going blank after extended use',
          description: 'The Home tab would sometimes show "No content available" after the app had been open for a few hours, even with a full library. Fixed. Your content now stays visible if a refresh fails, with a retry option instead of a blank screen.',
          platform: 'android'
        },
        {
          title: 'Fixed: Android Auto losing playback position on resume',
          description: 'When resuming a book through Android Auto after the app\'s auth token had expired, playback would restart from the beginning instead of picking up where you left off. The app now falls back to your locally saved position when the server is temporarily unreachable.',
          platform: 'android'
        },
        {
          title: 'Fixed: Bluetooth and Android Auto buttons always jumping chapters',
          description: 'Hardware media buttons (next/previous track) were ignoring your media controls setting and always jumping to the next or previous chapter. They now match your on-screen button behavior — skipping forward/back in seek mode, or jumping chapters in chapter mode.',
          platform: 'android'
        },
        {
          title: 'Fixed: Listening sessions showing wrong timestamps in stats',
          description: 'Sessions in the detailed stats view were displaying the sync time instead of the actual listening time, making yesterday\'s sessions appear as "Today" after a server sync.',
          platform: 'android'
        },
        {
          title: 'Improved: Playback speed +/- buttons on iOS',
          description: 'The playback speed sheet on iOS now includes + and - buttons for precise 0.1× adjustments, making it easier to dial in your preferred speed without relying solely on the slider.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Series cover images not showing when offline',
          description: 'Series cover images now display correctly when you\'re offline instead of appearing blank.',
          platform: 'ios'
        },
        {
          title: 'Improved: Tap current book in CarPlay to open Now Playing',
          description: 'Tapping the book you\'re currently listening to in CarPlay now navigates directly to the Now Playing screen instead of restarting playback.',
          platform: 'ios'
        },
        {
          title: 'Fixed: CarPlay ignoring saved playback speed',
          description: 'Resuming playback through CarPlay now correctly uses your saved playback speed instead of resetting to 1×.',
          platform: 'ios'
        },
        {
          title: 'Improved: Offline experience with better caching',
          description: 'Collections, Series, Authors, and library stats now display cached data when you\'re offline instead of showing errors. The Home screen also loads reliably on cold start without a network connection, and author detail views show a friendly offline message instead of a blank screen.',
          platform: 'android'
        },
        {
          title: 'Fixed: Progress loss when the app is stopped',
          description: 'The app now saves your playback position when the service is stopped, preventing progress loss if the system kills the app in the background.',
          platform: 'android'
        },
        {
          title: 'Fixed: Series sorting for episode ranges',
          description: 'Series with episode range numbers like "# 13 - 17" now sort correctly instead of being placed out of order.',
          platform: 'android'
        },
      ]
    },
    {
      version: '1.2.3',
      date: 'March 12, 2026',
      status: 'now-live',
      changes: [
        {
          title: 'Full podcast support',
          description: 'Audra now has complete podcast support. Search for podcasts by name or paste any RSS feed URL to add them directly to your Audiobookshelf server. Browse your podcast library, view episode feeds, and manage everything from the app — including editing podcast metadata (title, author, description, genres, and more), configuring auto-downloads with custom schedules and retention limits, downloading episodes to your server or device for offline listening, marking episodes as played, adding episodes to playlists, and unfollowing podcasts to remove them from your server entirely. Podcasts also appear in the Home tab with Continue Listening and Recently Added carousels.',
          platform: 'ios'
        },
        {
          title: 'Podcast carousels on the Home screen',
          description: 'Four new Home screen carousels for podcasts: Continue Listening, Recently Added, Newest Episodes, and Finished. They\'re off by default — enable them in Settings > Home Screen.',
          platform: 'ios'
        },
        {
          title: 'Settings icon moved to top right on Home screen',
          description: 'When podcasts are enabled, the Settings icon moves to the top right of the Home screen to make room for the podcast tab.',
          platform: 'ios'
        },
        {
          title: 'Podcast stats',
          description: 'Your listening stats now include podcasts. Filter your stats by audiobooks or podcasts to see episodes completed, your most listened podcast, and more.',
          platform: 'ios'
        },
        {
          title: 'New setting: Show or hide podcasts',
          description: 'If you only use Audra for audiobooks, you can now turn off podcast features entirely from Settings. This hides the Podcasts tab and keeps your library view focused on books.',
          platform: 'ios'
        },
        {
          title: 'Bulk episode management',
          description: 'Select multiple episodes at once and download them to your server, save them to your device for offline listening, or delete them — all in one tap.',
          platform: 'ios'
        },
        {
          title: 'Auto-download with configurable schedules',
          description: 'Set Audra to automatically check for and download new episodes on your schedule — from hourly to weekly. You can also cap how many episodes are kept, so older ones are removed automatically when the limit is reached.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Blank Now Playing screen when starting a book via CarPlay',
          description: 'Starting a book through CarPlay no longer sometimes opens a blank Now Playing screen on your CarPlay display.',
          platform: 'ios'
        },
        {
          title: 'Tap back once to restart chapter, twice to skip back',
          description: 'Pressing the previous chapter button now restarts the current chapter if you\'re more than 3 seconds in. Press it again to jump to the previous chapter — the same behavior you\'re used to on Android.',
          platform: 'ios'
        },
        {
          title: 'Fixed: ePub reader closing when selecting a chapter from the Table of Contents',
          description: 'Tapping a chapter in the Table of Contents no longer unexpectedly closes the reader. Navigation now works as expected.',
          platform: 'ios'
        }
      ]
    },
    {
      version: '1.2.2',
      date: 'March 5, 2026',
      status: 'now-live',
      changes: [
        {
          title: 'Android Auto support',
          description: 'Listen while you drive with full Android Auto integration. Browse your library and Continue Listening directly from your car\'s dashboard. Cover art, chapter names, and playback controls are all available on screen.',
          platform: 'android'
        },
        {
          title: 'New playback speed controls',
          description: 'The speed picker has been redesigned with dedicated + and − buttons and haptic feedback, making it easier to dial in exactly the speed you want.',
          platform: 'android'
        },
        {
          title: 'New setting: Media Controls',
          description: 'Choose what buttons appear in your notification shade and on your lock screen. Pick between seek buttons (skip forward/back) or chapter navigation buttons — find it in Settings > Playback.',
          platform: 'android'
        },
        {
          title: 'New setting: Seek Bar Progress',
          description: 'Choose whether the seek bar in your notification shade and Android Auto shows your progress through the whole book or just the current chapter. Find it in Settings > Playback.',
          platform: 'android'
        },
        {
          title: 'Double back press to restart or skip back',
          description: 'Press back once while playing to restart the current chapter. Press again to jump to the previous chapter.',
          platform: 'android'
        },
        {
          title: 'Book details opens fully expanded',
          description: 'The book details sheet now opens at full height, giving you more room to read the description and access options without an extra swipe.',
          platform: 'android'
        },
        {
          title: 'Accent color applied throughout the app',
          description: 'Your chosen accent color is now applied consistently across the entire app.',
          platform: 'android'
        },
        {
          title: 'Fixed: Chromecast session dropping after sleep',
          description: 'Chromecast sessions now resume automatically after your phone enters sleep or doze mode.',
          platform: 'android'
        },
        {
          title: 'Fixed: Offline playback for downloaded audiobooks',
          description: 'Fixed an edge case where downloaded audiobooks would not play correctly when offline on certain network configurations.',
          platform: 'android'
        },
        {
          title: 'Fixed: App stays logged in when resuming from background',
          description: 'Authentication tokens are now refreshed in the background, preventing unexpected sign-out errors when returning to the app.',
          platform: 'android'
        },
        {
          title: 'Fixed: Local sessions preserved when server is unreachable',
          description: 'Fixed an edge case where your local playback session could be wiped if the server was unreachable during a sync.',
          platform: 'android'
        },
        {
          title: 'Fixed: Library picker and offline indicator',
          description: 'The library picker sheet and offline status indicator now respond correctly in all cases.',
          platform: 'android'
        },
        {
          title: 'Improved offline error messages',
          description: 'Error messages in the player and reader are now displayed as polished themed dialogs instead of raw error text.',
          platform: 'android'
        },
        {
          title: 'Updated Media3 to 1.9.2',
          description: 'Upgraded the underlying media playback library for improved stability and performance.',
          platform: 'android'
        }
      ]
    },
    {
      version: '1.2.1',
      date: 'February 19, 2026',
      status: 'now-live',
      changes: [
        {
          title: 'Improved multi-device progress sync',
          description: 'Progress sync is more reliable when listening across multiple devices. If you switch to another device mid-book, your position updates automatically.',
          platform: 'ios'
        }
      ]
    },
    {
      version: '1.2.0',
      date: 'February 12, 2026',
      status: 'now-live',
      changes: [
        {
          title: 'Chromecast support',
          description: 'Cast audiobooks to any Chromecast-enabled device. Progress and controls stay in sync.',
          platform: 'android'
        },
        {
          title: 'Dynamic player background',
          description: 'New option in Settings > Appearance to show an animated gradient background on the Now Playing screen using colors from the book\'s cover art.',
          platform: 'both'
        },
        {
          title: 'Reordered playback controls',
          description: 'Skip forward/backward buttons are now positioned closer to the play/pause button, with chapter navigation buttons on the outside. This makes quick skips easier to reach.',
          platform: 'both'
        },
        {
          title: 'Fixed: Home tab images flickering on tab switch',
          description: 'Book covers no longer flash or show loading placeholders when switching to the Home tab. Images now appear instantly from cache, with background refreshes happening silently.',
          platform: 'ios'
        },
        {
          title: 'Improved background session reliability',
          description: 'Under-the-hood improvements to how the app maintains your login session.',
          platform: 'both'
        },
        {
          title: 'Fixed: Downloads disappearing after session expiry',
          description: 'Downloaded audiobooks are now preserved when your session expires and you log back in. Previously, a forced logout would permanently delete all downloaded files from the device.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Progress sync failing for non-admin users',
          description: 'Resolved an issue where progress refresh used an admin-only server endpoint, causing repeated "Access forbidden" errors for non-admin users. Progress now syncs correctly for all account types.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Crash when playing downloaded books on cellular',
          description: 'Playing a downloaded audiobook while on cellular with a LAN-only server no longer crashes the app. The app now detects unreachable servers quickly and plays locally, syncing progress when back on WiFi.',
          platform: 'android'
        },
        {
          title: 'Fixed: "Listen Again" not starting playback',
          description: 'Tapping "Listen Again" on a finished book now correctly starts playback from the beginning instead of briefly playing then stopping.',
          platform: 'both'
        },
        {
          title: 'Customizable skip intervals',
          description: 'You can now choose how many seconds the skip forward and skip backward buttons jump. Options include 10, 15, 30, 45, and 60 seconds. Find it in Settings > Playback.',
          platform: 'both'
        },
        {
          title: 'Fixed: Bluetooth headphone buttons skipping chapters',
          description: 'Bluetooth headphone next/previous buttons now skip forward 30 seconds or backward 15 seconds instead of jumping entire chapters.',
          platform: 'both'
        },
        {
          title: 'Fixed: Author cards misaligned in Newest Authors',
          description: 'Author cards in the Newest Authors section on the Home tab now align properly when names wrap to multiple lines.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Crash when selecting multiple libraries',
          description: 'Resolved an issue where the app could crash when browsing with more than one library selected, if those libraries contained overlapping authors or series.',
          platform: 'ios'
        }
      ]
    },
    {
      version: '1.1.9',
      date: 'February 2, 2026',
      status: 'now-live',
      changes: [
        // Features & Improvements
        {
          title: 'Completely revamped Settings',
          description: 'Settings has been redesigned from the ground up. Every individual setting is now searchable, and sections are reorganized for easier navigation.',
          platform: 'both'
        },
        {
          title: 'Improved search results',
          description: 'Search now shows matching authors, series, and narrators alongside book results. Tap an author or series to jump to their page, or tap a narrator to filter your library. The search placeholder also adapts to whichever tab you\'re on.',
          platform: 'both'
        },
        {
          title: 'Search loading indicator',
          description: 'A "Searching..." indicator now appears while search results are loading, replacing the brief flash of "No Results Found" that could occur before results arrived.',
          platform: 'both'
        },
        {
          title: 'Redesigned filter experience',
          description: 'Filters now open in a full-screen bottom sheet with collapsible sections. New Narrator and Language filters let you find exactly what you\'re looking for.',
          platform: 'both'
        },
        {
          title: '5 new color themes',
          description: 'Added Captain\'s Log, Neon Dreams, Velvet Noir, Cosmic Ice, and Vintage Leather themes to personalize your app.',
          platform: 'both'
        },
        {
          title: 'Improved theme readability in light mode',
          description: 'Multiple themes now adapt their colors for better contrast in light mode while preserving the original look in dark mode.',
          platform: 'both'
        },
        {
          title: 'Configurable book page layout',
          description: 'Choose which metadata appears on the book page vs. the Details sheet. Toggle visibility for Series, Genres, Published Year, Publisher, and Tags in Settings > Book Page Layout or via the gear icon in the Details sheet.',
          platform: 'both'
        },
        {
          title: 'Refreshed book detail layout',
          description: 'Reorganized the book detail view to make primary actions more prominent. Most metadata has moved into the Details sheet, which is now configurable — choose exactly what shows on the main book page vs. the sheet.',
          platform: 'both'
        },
        {
          title: 'Speed-adjusted duration display',
          description: 'Durations throughout the app — in book details, chapter listings, and the player view — now adjust based on your playback speed. Toggle this from Settings > Playback, or tap the duration badge in book details as a shortcut. Your preference is saved and applies across all books.',
          platform: 'both'
        },
        {
          title: '"Listen Again" for finished books',
          description: 'The play button now shows "Listen Again" with a replay icon when you\'ve completed a book.',
          platform: 'both'
        },
        {
          title: 'Mark Finished toggle redesign',
          description: 'The Mark Finished pill is now a toggle button. Shows an empty circle when unfinished, a filled checkmark when done. Tap to mark finished, tap again to mark as unread.',
          platform: 'both'
        },
        {
          title: 'Time remaining in player progress bar',
          description: 'Speed-adjusted book time remaining now displays in the middle of the progress bar. Configure position (Bottom, Middle, or Both) in Settings > Player.',
          platform: 'both'
        },
        {
          title: 'OLED Black toggle',
          description: 'New toggle in Settings > Appearance (visible in dark mode) lets you switch between pure black backgrounds for OLED screens and a softer dark gray. Defaults to OLED black on.',
          platform: 'ios'
        },
        // Bug Fixes
        {
          title: 'Fixed: Crash when starting playback',
          description: 'Resolved a crash that could occur when hitting play, caused by invalid audio time values during player initialization.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Login failing when server uses subpath URL',
          description: 'Servers running at a subpath (e.g., https://server/audiobookshelf) now work correctly. Previously, the app would drop the path and fail to connect.',
          platform: 'android'
        },
        {
          title: 'Fixed: Custom headers not applied to audio streaming',
          description: 'Custom authentication headers configured in Advanced Sign-In are now correctly applied to audio streaming requests, fixing playback issues behind certain reverse proxies.',
          platform: 'android'
        },
        {
          title: 'Fixed: Stats tab crashing on pull-to-refresh',
          description: 'Resolved an issue where rapidly refreshing or switching time periods on the Stats tab could cause cascading errors and eventually crash the app.',
          platform: 'android'
        },
        {
          title: 'Fixed: Player controls clipping with Display Zoom',
          description: 'Transport buttons (skip back/forward) no longer clip off screen edges when Display Zoom is set to "Larger". Bottom control labels like "Bookmark" no longer wrap to two lines under larger text or zoom settings.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Rewind/seek backward not syncing to server',
          description: 'Seeking or rewinding backward now correctly syncs the new position to the server. Previously, backward seeks were ignored and the app would jump back to the old position when returning from the background.',
          platform: 'both'
        },
        {
          title: 'Fixed: Downloaded books not playing offline for some users',
          description: 'Resolved an edge case where downloaded audiobooks could show "You\'re offline" errors instead of playing. Downloaded books now play directly from local files without requiring any server connection.',
          platform: 'android'
        }
      ]
    },
    {
      version: '1.1.8',
      date: 'January 24, 2026',
      status: 'now-live',
      changes: [
        {
          title: 'Custom HTTP headers for proxy authentication',
          description: 'Add custom headers in Advanced Sign-In for Cloudflare Zero Trust, Pangolin, or other reverse proxies.',
          platform: 'android'
        },
        {
          title: 'Fixed: Images, streaming, and downloads now work behind proxies',
          description: 'Custom authentication headers are now applied to all requests.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Books Completed not showing all finished books',
          description: 'The Books Completed card on My Stats was unreliable and only showed a subset of finished books. It now correctly displays all completed books with time period filtering.',
          platform: 'both'
        },
        {
          title: 'More auto-rewind options',
          description: 'Auto-rewind on resume now offers 10, 15, 30, and 45 second options for more flexibility.',
          platform: 'both'
        },
        {
          title: 'Continue Listening updates faster',
          description: 'New books now appear in Continue Listening immediately when you switch tabs or return to the app.',
          platform: 'both'
        },
        {
          title: 'Fixed: Libraries not loading for some users',
          description: 'Fixed an issue where libraries would fail to load with a "coverAspectRatio missing" error. The app now handles optional library settings more gracefully.',
          platform: 'android'
        },
        {
          title: 'Improved UI on foldable and narrow screens',
          description: 'Settings toggles, library tabs, and filter controls now display properly on devices with narrow displays like foldable cover screens.',
          platform: 'android'
        },
        {
          title: 'Fixed: Extended book metadata no longer disappears',
          description: 'Author, narrator, and series number now persist correctly after viewing a book\'s details. Previously, this information could disappear when navigating away and back to the books list.',
          platform: 'android'
        },
        {
          title: 'Redesigned Now Playing screen',
          description: 'The main player has been completely redesigned with a cleaner, more polished layout that fits on screen without scrolling. Playback controls now feature smooth animations and haptic feedback.',
          platform: 'both'
        },
        {
          title: 'Sleep timer chapter selection',
          description: 'The sleep timer now lets you select any chapter to stop at, not just the current one. Perfect for falling asleep at a natural stopping point.',
          platform: 'both'
        },
        {
          title: 'Fixed: Chapter time showing total instead of remaining in Both mode',
          description: 'When timeline mode is set to "Both", the chapter progress bar now correctly shows remaining time instead of total chapter duration.',
          platform: 'both'
        },
        {
          title: 'Fixed: App crash when server is unreachable',
          description: 'The app no longer crashes when opened without server connectivity (WiFi without internet, VPN disconnected, or server offline). Instead, it now gracefully shows cached content while indicating the server is unreachable.',
          platform: 'android'
        },
        {
          title: 'Fixed: Audiobooks marked finished with content remaining',
          description: 'Long audiobooks were being marked as finished too early (at 95-98% progress), leaving up to 30+ minutes unplayed. Books are now marked finished only when 30 seconds or less remain.',
          platform: 'both'
        },
        {
          title: 'Configurable auto-complete threshold',
          description: 'New setting to control when audiobooks are marked as finished. Choose from: end of book, 10 seconds, 30 seconds (default), 1 minute, or 2 minutes remaining.',
          platform: 'both'
        },
        {
          title: 'Fixed: "Show Home Tab" setting not persisting',
          description: 'The "Show Home Tab" setting now correctly persists after closing and reopening the app.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Lock screen cover art delay',
          description: 'Cover art now appears immediately on the lock screen and Now Playing controls instead of taking several seconds to load.',
          platform: 'ios'
        },
        {
          title: 'Chapter selection from Now Playing',
          description: 'Tap the chapter name on the main player screen to quickly jump to any chapter in the book.',
          platform: 'both'
        }
      ]
    },
    {
      version: '1.1.7',
      date: 'January 17, 2026',
      status: 'now-live',
      changes: [
        {
          title: 'Note: Clear app cache if syncing issues occur',
          description: 'If you experience syncing issues after updating, go to Settings and clear your app cache.',
          platform: 'both',
          highlight: true
        },
        {
          title: 'Fixed: Multi-file audiobooks now play continuously',
          description: 'Multi-file audiobooks now play through all tracks seamlessly instead of stopping after the first file. Chapter navigation and progress tracking work correctly.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Multi-file audiobooks no longer marked finished after first chapter',
          description: 'Multi-file audiobooks were incorrectly being marked as "finished" after the first chapter ended. Progress now tracks correctly across all chapters.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Home screen now refreshes automatically',
          description: 'Continue Listening and Recently Added now update automatically when you return to the Home tab.',
          platform: 'ios'
        },
        {
          title: 'Fixed: Progress sync no longer overwrites newer data from other devices',
          description: 'When playing on another device (ABS web or official app) and returning to Audra, your newer progress is now preserved. Previously, Audra could overwrite the server with older local progress.',
          platform: 'both'
        },
        {
          title: 'Book detail view shows real-time progress updates',
          description: 'When viewing a book\'s detail page, progress now updates automatically every 10 seconds to reflect changes made on other devices.',
          platform: 'both'
        },
        {
          title: 'Auto-rewind on resume',
          description: 'Automatically rewind by 5, 10, or 15 seconds when resuming playback after pausing. Perfect for getting back into the story after interruptions. Includes an optional setting to only rewind after long pauses (5+ minutes).',
          platform: 'both'
        },
        {
          title: 'Faster loading for users with collections',
          description: 'Collections now load much faster and use significantly less data. If you have many collections on your home screen, you\'ll notice a big improvement in loading times.',
          platform: 'both'
        },
        {
          title: 'Fixed: Crash when scrolling to bottom of home screen',
          description: 'Fixed an issue that could cause the app to crash when scrolling to the bottom of the home screen for some users.',
          platform: 'android'
        },
        {
          title: 'Faster library tab switching',
          description: 'Series, Authors, Collections, and Playlists tabs are now cached for faster loading while staying up-to-date with your server.',
          platform: 'both'
        },
        {
          title: 'Fixed: Book covers now refresh when changed on server',
          description: 'When you change a book\'s cover in Audiobookshelf, it now updates in the app when switching tabs. Previously, the old cover would persist until you cleared the cache or restarted the app.',
          platform: 'both'
        },
        {
          title: 'Fixed: Multi-library selection now works for Series, Authors, and Collections',
          description: 'When multiple libraries are selected, the Series, Authors, and Collections tabs now correctly display combined data from all selected libraries instead of only the first one.',
          platform: 'both'
        },
        {
          title: 'Improved CarPlay reliability',
          description: 'CarPlay now launches faster and no longer gets stuck on a blank screen.',
          platform: 'ios'
        }
      ]
    },
    {
      version: '1.1.6',
      date: 'January 10, 2026',
      status: 'now-live',
      changes: [
        {
          title: 'Fixed: Audra Pro not syncing across Android devices',
          description: 'Resolved an issue where Audra Pro purchases weren\'t being recognized on other Android devices signed in with the same Google account.',
          platform: 'android'
        },
        {
          title: 'Toggle to disable AI-generated author previews',
          description: 'New setting to turn off AI-generated author descriptions.',
          platform: 'both'
        }
      ]
    },
    // Android Releases
    {
      version: 'Build 18',
      date: 'December 23, 2025',
      platform: 'android',
      status: 'now-live',
      changes: [
        {
          title: 'Background playback permission prompt',
          description: 'On first launch, the app now prompts you to allow background playback. This helps prevent Android from stopping your audiobook when the screen is off. You can also enable this anytime from Settings.'
        },
        {
          title: 'Improved offline experience',
          description: 'When you go offline, all your Home screen content now stays visible instead of disappearing. Browse your Continue Listening, Recently Added, and other sections while offline.'
        },
        {
          title: 'Better connection status indicators',
          description: 'The Settings screen now accurately shows your server connection status. You\'ll see "No Connection" (red) when offline, "Server Unreachable" (orange) when your server is down, or "Connected" (green) when everything\'s working.'
        },
        {
          title: 'Server status banner on Home screen',
          description: 'A blue "Currently Offline" or orange "Server Unavailable" banner now appears at the top of your Home screen when there are connection issues, matching the iOS experience.'
        },
        {
          title: 'Fixed: Play button not working after long pause',
          description: 'Fixed an issue where the play button would stop responding after pausing for 30+ minutes. Audio now resumes reliably even after extended pauses.'
        },
        {
          title: 'Fixed: Crash when trying to play offline',
          description: 'Trying to play an audiobook while offline no longer crashes the app. Instead, you\'ll see a clear message explaining that the audiobook needs to be downloaded for offline listening.'
        }
      ]
    },
    {
      version: 'Build 17',
      date: 'December 21, 2025',
      platform: 'android',
      status: 'now-in-beta',
      changes: [
        {
          title: 'Android 16 background playback compatibility',
          description: 'Updated to Media3 1.9.0 and added defensive error handling to prevent audio from stopping when the app is in the background on Android 16 devices.'
        },
        {
          title: 'Offline mode with automatic reconnection',
          description: 'The app now gracefully handles being offline. A banner shows when you\'re offline, cached content loads automatically, and everything refreshes when you\'re back online.'
        },
        {
          title: 'Your library selections now persist',
          description: 'Selected libraries are saved and restored when you reopen the app, so you don\'t have to reselect them every time.'
        },
        {
          title: 'Playback speed persists across restarts',
          description: 'Your preferred playback speed is now saved and automatically restored when you start the app.'
        },
        {
          title: 'Configurable "NEW" badge duration',
          description: 'New setting to control how long the "NEW" badge appears on recently added items. Choose from Off, 7, 30, or 90 days.'
        },
        {
          title: 'Fixed: Consistent card sizing in library grid',
          description: 'Ebook-only items now display with the same square card size as audiobooks, eliminating inconsistent row heights in carousels and the library grid.'
        },
        {
          title: 'Fixed: SSO login for users with ebook-only progress',
          description: 'Resolved a bug where SSO login would fail for users who had reading progress on ebooks but no audiobook listening history.'
        },
        {
          title: 'Fixed: OAuth re-login after logout',
          description: 'Logging out and back in with SSO/OAuth now works correctly. Previously, stale session cookies could cause authentication failures.'
        },
        {
          title: 'Fixed: OAuth race condition',
          description: 'Prevented issues from rapidly tapping the SSO button by adding proper flow management and a 5-minute timeout.'
        },
        {
          title: 'Change password from within the app',
          description: 'You can now change your Audiobookshelf password directly from Settings.'
        },
        {
          title: 'Reorder home screen sections',
          description: 'Drag and drop to rearrange the sections on your home screen. Your custom order is saved and persists across app restarts.'
        },
        {
          title: 'Several small UI and usability fixes',
          description: 'Various improvements including better theme consistency, fixed bottom padding on detail screens, and smoother settings interactions.'
        }
      ]
    },
    {
      version: 'Build 12',
      date: 'December 15, 2025',
      platform: 'android',
      status: 'now-in-beta',
      changes: [
        {
          title: 'Fixed: SSO login failing to open browser',
          description: 'Another fix for SSO/OIDC login. The app was unable to launch the browser for authentication on some devices. This should now work correctly.'
        },
        {
          title: 'Fixed: Series covers now display in correct order',
          description: 'The fanned book covers on series cards now display in sequence order (Book 1, 2, 3...) instead of random order.'
        }
      ]
    },
    {
      version: 'Build 11',
      date: 'December 15, 2025',
      platform: 'android',
      status: 'now-in-beta',
      changes: [
        {
          title: 'Revamped Series section',
          description: 'The Series tab now displays large book covers in a swipeable card deck. Swipe through the books in each series right from the list.'
        },
        {
          title: 'Offline playback now syncs progress to your server',
          description: 'When playing downloaded audiobooks, your listening progress is now saved locally and automatically synced to your server when you\'re back online.'
        },
        {
          title: 'Fixed: Library sorting now works correctly',
          description: 'Sorting by Title, Author, or Duration now properly updates the library view. Your sort preference is also saved and persists across app restarts.'
        },
        {
          title: 'Fixed: Book detail now shows all authors and narrators',
          description: 'Books with multiple authors or narrators now display all of them instead of just the first one.'
        },
        {
          title: 'Fixed: SSO login failing for some users',
          description: 'Resolved a bug that caused SSO/OIDC login to fail with a network error.'
        },
        {
          title: 'Fixed: Search bar usability issues',
          description: 'The search bar now stays visible when there are no results, so you can fix typos without clearing everything. The keyboard also no longer dismisses while typing.'
        },
        {
          title: 'Swipe down to dismiss the Now Playing screen',
          description: 'You can now swipe down on the Now Playing screen to close it, just like you\'d expect.'
        },
        {
          title: 'Consistent tab headers',
          description: 'All main tabs (Home, Library, Downloads, My Stats, Settings) now have consistent title styling that respects your chosen theme.'
        },
        {
          title: 'Series sequence numbers on book covers',
          description: 'Books in series now display their sequence number as a badge on the cover art, making it easy to see which book is next.'
        },
        {
          title: 'System media controls now work',
          description: 'Play/pause, skip, and rewind controls now appear in your notification shade, lock screen, and respond to Bluetooth headphone buttons.'
        },
        {
          title: 'Fixed: Several small UI inconsistencies',
          description: 'Various spacing and alignment fixes throughout the app for a more polished experience.'
        }
      ]
    },
    {
      version: 'Build 10',
      date: 'December 14, 2025',
      platform: 'android',
      status: 'now-in-beta',
      changes: [
        {
          title: 'Potential fix for download issues',
          description: 'Fixed unlimited storage setting causing downloads to fail. Please let us know if downloads work better for you now!'
        },
        {
          title: 'Debug logging for support',
          description: 'Added a Debug Logs section in Settings. You can now send logs to help diagnose issues. There\'s also a Send Logs button on the login screen for users who can\'t log in.'
        },
        {
          title: 'Check for Updates button',
          description: 'Added a Check for Updates button in Settings that links to this beta page.'
        },
        {
          title: 'Improved login screen',
          description: 'Better spacing and UX improvements. Server validation now shows visual feedback (checkmark or X) when you enter your server address.'
        },
        {
          title: 'Theme colors applied consistently',
          description: 'Progress indicators and the Downloads tab row now use the app\'s theme colors correctly.'
        }
      ]
    },
    {
      version: 'Build 9',
      date: 'December 14, 2025',
      platform: 'android',
      status: 'now-in-beta',
      changes: [
        {
          title: 'Recommended: Fresh install',
          description: 'Due to significant caching and API changes in this build, we recommend uninstalling the app and clearing data before installing Build 9 for the best experience.',
          highlight: true
        },
        {
          title: 'Better support for large libraries',
          description: 'Significant API changes to use proper pagination and server-side queries. The app no longer tries to load your entire library at once, which should fix issues for users with thousands of books.'
        },
        {
          title: 'Library now uses server-side search and filtering',
          description: 'Search and filters are now handled by your server instead of locally. Should be much faster, especially for large libraries.'
        },
        {
          title: 'Playback sessions now sync with your server',
          description: 'Your listening sessions will show up in your Audiobookshelf server\'s stats and sync more reliably across devices.'
        },
        {
          title: 'Stats screen improvements',
          description: 'Added touch scrubbing on charts and smoother animations. 1Y and All Time stats now pull directly from your server instead of fetching all sessions, so they load much faster. Default period is now 1 Week.'
        },
        {
          title: 'New Home screen sections and settings',
          description: 'Added "Continue Series" and "Newest Authors" sections to the home screen. New settings let you show/hide these sections, and there\'s also an option to hide section icons if you prefer a cleaner look.'
        },
        {
          title: 'Smoother tab switching in Library',
          description: 'Switching between Books, Authors, and Series tabs no longer flashes white—uses crossfade now.'
        },
        {
          title: 'Library defaults to 3-column grid',
          description: 'Changed the default from 2 columns to 3. You can still change this in settings.'
        },
        {
          title: 'Fixed: SSO/OIDC login issues',
          description: 'SSO should now work correctly with Authentik, Keycloak, Authelia, etc.'
        },
        {
          title: 'Fixed: Downloads getting stuck or showing wrong cache size',
          description: 'Download queue should no longer get stuck, and the cache size in settings now updates properly.'
        },
        {
          title: 'Fixed: Library search returning incomplete results',
          description: 'Search now queries your server directly instead of only searching cached items.'
        },
        {
          title: 'UI polish',
          description: 'Search bar, filter chips, and bottom nav updated to Material 3 components. Better theme integration throughout. Reduced excessive spacing above cover art in book detail view.'
        }
      ]
    },
    {
      version: 'Build 8',
      date: 'December 11, 2025',
      platform: 'android',
      status: 'now-in-beta',
      changes: [
        {
          title: 'Fixed: Downloads no longer get stuck at 100%',
          description: 'Downloads now complete properly and move to the Downloaded tab. Previously, books could get stuck showing 100% in the queue.'
        },
        {
          title: 'Fixed: "Size mismatch" download errors',
          description: 'Eliminated false "Size mismatch" errors that prevented some audiobooks from downloading successfully.'
        },
        {
          title: 'Fixed: Unlimited storage setting',
          description: 'Setting storage quota to "Unlimited" now works correctly. Previously it would show as 1GB.'
        },
        {
          title: 'Fixed: Pull-to-refresh now works correctly',
          description: 'Pull down to refresh on the home screen now properly updates Recently Added and Recently Released sections.'
        },
        {
          title: 'Improved: Book detail screen layout',
          description: 'Reduced excessive spacing above the cover art for a cleaner, more polished look.'
        },
        {
          title: 'Removed: Broken refresh on downloads screen',
          description: 'Removed the non-functional pull-to-refresh from the downloads screen. Downloads update automatically in real-time.'
        },
        {
          title: 'Added: HTTPS with self-signed certificates',
          description: 'You can now connect via HTTPS using self-signed certificates installed in your Android device\'s CA store.'
        },
        {
          title: 'Fixed: Compatibility with older Audiobookshelf servers',
          description: 'Resolved an issue where some users saw no books or libraries after logging in.'
        },
        {
          title: 'Fixed: Single Sign-On (SSO) now works',
          description: 'SSO/OIDC login with identity providers like Authentik, Keycloak, and Authelia now works correctly. Tap "Advanced Sign-In" to access SSO login.'
        },
        {
          title: 'Fixed: Library tab shows correct counts',
          description: 'The Authors and Series tabs now display the correct count in the header. Previously, they incorrectly showed the book count instead.'
        },
        {
          title: 'Fixed: Sleep timer now pauses with playback',
          description: 'The sleep timer now correctly pauses when you pause playback and resumes when you play. "End of Chapter" timers also update when you skip or seek through the chapter.'
        }
      ]
    },
    {
      version: 'Build 7',
      date: 'December 10, 2025',
      platform: 'android',
      status: 'now-in-beta',
      changes: [
        {
          title: 'Fixed: Books no longer reset to the beginning',
          description: 'Tapping a book from "Continue Listening" now correctly resumes where you left off instead of starting over.'
        },
        {
          title: 'Fixed: Chapter selection works correctly',
          description: 'Selecting a chapter from the chapter list now plays the correct chapter. Previously, multi-file audiobooks would jump to the wrong position.'
        },
        {
          title: 'Fixed: Progress syncs from server on new devices',
          description: 'When you install Audra on a new device, your listening progress from your Audiobookshelf server now shows immediately. The "Resume" button appears correctly with your saved position.'
        },
        {
          title: 'Added: HTTP server support',
          description: 'You can now connect to Audiobookshelf servers using HTTP, not just HTTPS. Useful for local network setups without SSL certificates.'
        },
        {
          title: 'Beta: Subscription paywall removed',
          description: 'All premium features are unlocked during the closed beta testing period.'
        }
      ]
    }
  ];

  const getStatusBadge = (status) => {
    const badges = {
      'coming-up': { label: 'Next Release', className: 'badge-coming-up' },
      'going-live': { label: 'Going Live', className: 'badge-going-live' },
      'submitted': { label: 'Submitted', className: 'badge-submitted' },
      'now-in-beta': { label: 'Beta', className: 'badge-beta' },
      'now-live': { label: 'Now Live', className: 'badge-live' },
      'planned': { label: 'Planned', className: 'badge-planned' },
      'in-progress': { label: 'In Progress', className: 'badge-progress' },
      'completed': { label: 'Completed', className: 'badge-completed' }
    };
    return badges[status] || badges['planned'];
  };

  const getPlatformBadge = (platform) => {
    const badges = {
      'android': { label: 'Android', icon: 'M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z' },
      'ios': { label: 'iOS', icon: 'M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z' },
      'both': { label: 'Both Platforms', icon: null }
    };
    return badges[platform] || badges['android'];
  };

  // hideAndroid / hideIos drop that platform's entries; 'both' entries show while either is live.
  const isLive = (release, platform) => {
    if (platform === 'android') return !release.hideAndroid;
    if (platform === 'ios') return !release.hideIos;
    return !(release.hideAndroid && release.hideIos);
  };

  const releaseNote = (release) => {
    if (release.hideAndroid && !release.hideIos) return release.singlePlatformNote?.ios ?? release.note;
    if (release.hideIos && !release.hideAndroid) return release.singlePlatformNote?.android ?? release.note;
    return release.note;
  };

  const filteredReleases = releaseNotes.filter(release => !release.hidden).map(release => {
    // Filter changes based on platform filter
    const filteredChanges = release.changes.filter(change => isLive(release, change.platform)).filter(change => {
      if (platformFilter === 'all') return true;
      if (platformFilter === 'android') return change.platform === 'android' || change.platform === 'both';
      if (platformFilter === 'ios') return change.platform === 'ios' || change.platform === 'both';
      return true;
    });

    // Only include release if it has changes after filtering
    return filteredChanges.length > 0 ? { ...release, note: releaseNote(release), changes: filteredChanges } : null;
  }).filter(release => release !== null);

  return (
    <>
      <Helmet>
        <title>Release Notes - Audra Books</title>
        <meta name="description" content="See what's new in Audra Books — the Audiobookshelf client for iOS and Android. Full changelog with features, improvements, and bug fixes for every release." />
        <meta name="keywords" content="Audra Books update, Audra release notes, Audra changelog, audiobookshelf iOS update, audiobookshelf Android update, audiobook app update" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://audrabooks.app/release-notes" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://audrabooks.app/release-notes" />
        <meta property="og:title" content="Release Notes - Audra Books" />
        <meta property="og:description" content="See what's new in Audra Books — the Audiobookshelf client for iOS and Android. Full changelog with features, improvements, and bug fixes for every release." />
        <meta property="og:image" content="https://audrabooks.app/images/og-image.png" />
        <meta property="og:site_name" content="Audra Books" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://audrabooks.app/release-notes" />
        <meta name="twitter:title" content="Release Notes - Audra Books" />
        <meta name="twitter:description" content="See what's new in Audra Books — the Audiobookshelf client for iOS and Android. Full changelog with features, improvements, and bug fixes for every release." />
        <meta name="twitter:image" content="https://audrabooks.app/images/og-image.png" />
      </Helmet>
      <div className="beta-page">
        <div className="beta-container">
          <header className="beta-header">
            <p className="beta-subtitle">Release Notes</p>
          </header>

          {/* Platform Filter */}
          <div className="platform-filter">
            <button
              className={`filter-button ${platformFilter === 'all' ? 'active' : ''}`}
              onClick={() => setPlatformFilter('all')}
            >
              All Platforms
            </button>
            <button
              className={`filter-button ${platformFilter === 'android' ? 'active' : ''}`}
              onClick={() => setPlatformFilter('android')}
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
              </svg>
              Android
            </button>
            <button
              className={`filter-button ${platformFilter === 'ios' ? 'active' : ''}`}
              onClick={() => setPlatformFilter('ios')}
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z"/>
              </svg>
              iOS
            </button>
          </div>

          {filteredReleases.map((release, releaseIndex) => {
            const badge = getStatusBadge(release.status);
            const releasePlatformBadge = release.platform ? getPlatformBadge(release.platform) : null;
            const useReleaseLevelBadge = release.platform && release.version.startsWith('Build');

            return (
              <section key={releaseIndex} className="release-section">
                <div className="release-header">
                  <span className={`status-badge ${badge.className}`}>
                    {badge.label}
                  </span>
                  {useReleaseLevelBadge && release.platform !== 'both' && (
                    <span className="platform-badge">
                      {releasePlatformBadge.icon && (
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d={releasePlatformBadge.icon} />
                        </svg>
                      )}
                      {releasePlatformBadge.label}
                    </span>
                  )}
                  <h2>{release.version}</h2>
                  <span className="release-date">{release.date}</span>
                </div>
                {release.note && (
                  <p className="release-note">{release.note}</p>
                )}
                <ul className="changes-list">
                  {(() => {
                    const features = release.changes.filter(c => !c.title.startsWith('Fixed:'));
                    const fixes = release.changes.filter(c => c.title.startsWith('Fixed:'));
                    const showHeaders = features.length > 0 && fixes.length > 0;
                    const grouped = showHeaders
                      ? [
                          { header: 'What\'s New', items: features },
                          { header: 'Bug Fixes', items: fixes }
                        ]
                      : [{ header: null, items: release.changes }];

                    return grouped.map((group, groupIndex) => (
                      <React.Fragment key={groupIndex}>
                        {group.header && (
                          <li className="changes-section-header">{group.header}</li>
                        )}
                        {group.items.map((change, changeIndex) => {
                    const showItemBadge = !useReleaseLevelBadge && change.platform;

                    return (
                      <li key={changeIndex} className={`change-item${change.highlight ? ' highlight' : ''}`}>
                        {showItemBadge && (
                          <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                            {change.platform === 'both' ? (
                              <>
                                <span className="platform-badge" style={{ fontSize: '0.7rem', padding: '2px 6px' }}>
                                  <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: '11px', height: '11px' }}>
                                    <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z" />
                                  </svg>
                                  iOS
                                </span>
                                <span className="platform-badge" style={{ fontSize: '0.7rem', padding: '2px 6px' }}>
                                  <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: '11px', height: '11px' }}>
                                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                                  </svg>
                                  Android
                                </span>
                              </>
                            ) : (
                              <span className="platform-badge" style={{ fontSize: '0.7rem', padding: '2px 6px' }}>
                                {getPlatformBadge(change.platform).icon && (
                                  <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: '11px', height: '11px' }}>
                                    <path d={getPlatformBadge(change.platform).icon} />
                                  </svg>
                                )}
                                {getPlatformBadge(change.platform).label}
                              </span>
                            )}
                          </div>
                        )}
                        <strong>{change.title}</strong>
                        <p>
                          {change.description}
                          {change.descriptionSuffix}
                        </p>
                      </li>
                    );
                  })}
                      </React.Fragment>
                    ));
                  })()}
                </ul>
              </section>
            );
          })}

          <footer className="beta-footer">
            <p>Thank you for using Audra!</p>

            <p>Found a bug or have feedback?</p>
            <div className="feedback-buttons">
              <a
                href="https://github.com/RandallEric/AudraBooks-Feedback/issues"
                target="_blank"
                rel="noopener noreferrer"
                className="feedback-link"
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                </svg>
                GitHub Issues
              </a>
            </div>
          </footer>
        </div>
      </div>
    </>
  );
}

export default Beta;
